import { DOCUMENT } from '@angular/common';
import { Directive, ElementRef, HostBinding, HostListener, Inject, Input, OnDestroy, Renderer2 } from '@angular/core';

@Directive({
  selector: '[ssTooltip], [ssTooltipRoot]',
  standalone: false
})
export class TooltipDirective implements OnDestroy {
  @Input() ssTooltip = '';
  @HostBinding('attr.data-ss-own') get own(): string | null { return this.ssTooltip ? '' : null; }

  private static nextId = 0;
  private tooltip?: HTMLElement;
  private target?: HTMLElement;
  private removeListeners: (() => void)[] = [];
  private describedBy?: string | null;
  private storedTitle?: string | null;
  private lastTouchAt = 0;
  private longPressed = false;
  private longPressTimer?: ReturnType<typeof setTimeout>;
  private autoHideTimer?: ReturnType<typeof setTimeout>;

  private static readonly emulatedMouseWindow = 800;
  private static readonly longPressDelay = 450;
  private static readonly touchDuration = 2200;
  private static readonly iconControl = '.ss-btn-icon, .ss-icon-button';

  constructor(
    private host: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) { }

  @HostListener('mouseover', ['$event.target'])
  @HostListener('focusin', ['$event.target'])
  show(element: EventTarget | null) {
    if (Date.now() - this.lastTouchAt < TooltipDirective.emulatedMouseWindow) return;
    this.open(element);
  }

  @HostListener('touchstart', ['$event'])
  startLongPress(event: TouchEvent) {
    this.lastTouchAt = Date.now();
    this.cancelLongPress();
    const element = event.target;
    if (!(element instanceof Element) || !this.resolve(element)) return;
    this.longPressTimer = setTimeout(() => {
      this.longPressTimer = undefined;
      this.longPressed = true;
      this.open(element);
      this.autoHideTimer = setTimeout(() => this.hide(), TooltipDirective.touchDuration);
    }, TooltipDirective.longPressDelay);
  }

  @HostListener('touchmove')
  @HostListener('touchcancel')
  cancelLongPress() {
    if (this.longPressTimer) clearTimeout(this.longPressTimer);
    this.longPressTimer = undefined;
  }

  @HostListener('touchend', ['$event'])
  endLongPress(event: TouchEvent) {
    this.lastTouchAt = Date.now();
    this.cancelLongPress();
    if (!this.longPressed) return;
    this.longPressed = false;
    event.preventDefault();
  }

  @HostListener('contextmenu', ['$event'])
  blockContextMenu(event: Event) {
    if (this.longPressed || this.tooltip) event.preventDefault();
  }

  @HostListener('mouseout', ['$event'])
  @HostListener('focusout', ['$event'])
  leave(event: MouseEvent | FocusEvent) {
    if (Date.now() - this.lastTouchAt < TooltipDirective.emulatedMouseWindow) return;
    if (this.target && (!(event.relatedTarget instanceof Node) || !this.target.contains(event.relatedTarget))) this.hide();
  }

  ngOnDestroy() {
    this.cancelLongPress();
    this.hide();
  }

  private resolve(element: Element): { target: HTMLElement; text: string } | null {
    if (this.ssTooltip) return { target: this.host.nativeElement, text: this.ssTooltip };
    const target = element.closest<HTMLElement>(`[data-ss-tooltip], ${TooltipDirective.iconControl}`);
    if (!target || target.hasAttribute('data-ss-own')) return null;
    const text = target.getAttribute('data-ss-tooltip') || target.getAttribute('aria-label') || target.getAttribute('title');
    return text ? { target, text } : null;
  }

  private open(element: EventTarget | null) {
    if (!(element instanceof Element)) return;
    const resolved = this.resolve(element);
    if (!resolved || !this.host.nativeElement.contains(resolved.target) || resolved.target.matches(':disabled, [aria-disabled="true"]')) return;
    const { target, text } = resolved;
    if (this.target === target) return;

    this.hide();
    this.target = target;
    this.storedTitle = target.getAttribute('title');
    if (this.storedTitle !== null) target.removeAttribute('title');
    this.tooltip = this.renderer.createElement('div');
    const tooltip = this.tooltip!;
    tooltip.id = `ss-tooltip-${++TooltipDirective.nextId}`;
    tooltip.className = 'ss-tooltip';
    tooltip.textContent = text;
    tooltip.setAttribute('role', 'tooltip');
    this.describedBy = target.getAttribute('aria-describedby');
    target.setAttribute('aria-describedby', [this.describedBy, tooltip.id].filter(Boolean).join(' '));
    this.document.body.appendChild(tooltip);
    this.position();
    this.removeListeners = [
      this.renderer.listen('window', 'resize', () => this.hide()),
      this.renderer.listen(this.document, 'scroll', () => this.hide(), { capture: true }),
      this.renderer.listen(this.document, 'keydown', (event: KeyboardEvent) => {
        if (event.key === 'Escape') this.hide();
      })
    ];
  }

  private position() {
    if (!this.tooltip || !this.target) return;
    const box = this.target.getBoundingClientRect();
    const tip = this.tooltip.getBoundingClientRect();
    const view = this.document.defaultView!;
    const gap = parseFloat(getComputedStyle(this.tooltip).paddingTop);
    const left = Math.max(gap, Math.min(box.left + (box.width - tip.width) / 2, view.innerWidth - tip.width - gap));
    const top = box.top >= tip.height + gap ? box.top - tip.height - gap : box.bottom + gap;
    this.renderer.setStyle(this.tooltip, 'left', `${left}px`);
    this.renderer.setStyle(this.tooltip, 'top', `${Math.max(gap, Math.min(top, view.innerHeight - tip.height - gap))}px`);
  }

  private hide() {
    if (this.autoHideTimer) clearTimeout(this.autoHideTimer);
    this.autoHideTimer = undefined;
    this.removeListeners.forEach((remove) => remove());
    this.removeListeners = [];
    if (this.target) {
      if (this.describedBy) this.target.setAttribute('aria-describedby', this.describedBy);
      else this.target.removeAttribute('aria-describedby');
    }
    if (this.target && this.storedTitle !== null && this.storedTitle !== undefined) this.target.setAttribute('title', this.storedTitle);
    this.storedTitle = undefined;
    this.tooltip?.remove();
    this.tooltip = undefined;
    this.target = undefined;
  }
}
